import { Email } from '@/assets/icons/ats';
import { KTag, KText } from '@/ui-library/components';
import { Avatar, Icon, Tooltip, Flex, Center, Link } from '@chakra-ui/react';
import { CheckCircle, Clock } from '@kula-ai/icons-core';
import { HiOutlinePhone } from 'react-icons/hi';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  BsGlobe,
  BsFacebook,
  BsGithub,
  BsInstagram,
  BsLinkedin,
  BsStackOverflow,
  BsTwitterX,
} from 'react-icons/bs';
import { Greenhouse } from '@/assets/icons/colored';
import { Lever } from '@/assets/icons/colored';
import { Ashby } from '@/assets/icons/colored';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { REGEX_PATTERNS } from '@/constants/regex';
const candidateLinksConfig = [
  {
    id: 'linkedin_url',
    icon: BsLinkedin,
    color: ' #2D64BC',
    label: 'Linkedin',
    regex: REGEX_PATTERNS.linkedin,
    tooltip: 'Linkedin',
  },
  {
    id: 'twitter_url',
    icon: BsTwitterX,
    label: 'Twitter',
    regex: /^(https?:\/\/)?(www\.)?twitter\.com\/[a-zA-Z0-9_]+(\/)?$/,
    tooltip: 'Twitter',
  },
  {
    id: 'github_url',
    icon: BsGithub,
    label: 'Github',
    regex: /^(https?:\/\/)?(www\.)?github\.com\/[a-zA-Z0-9_-]+(\/)?$/,
    tooltip: 'Github',
  },
  {
    id: 'stackoverflow_url',
    icon: BsStackOverflow,
    label: 'Stackoverflow',
    regex:
      /^(https?:\/\/)?(www\.)?stackoverflow\.com\/users\/[0-9]+\/[a-zA-Z0-9_-]+(\/)?$/,
    color: '#e7700d',
    tooltip: 'Stackoverflow',
  },
  {
    id: 'facebook_url',
    icon: BsFacebook,
    label: 'Facebook',
    regex: /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9.]+(\/)?$/,
    color: '#1877F2',
    tooltip: 'Facebook',
  },
  {
    id: 'instagram_url',
    icon: BsInstagram,
    label: 'Instagram',
    regex: /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+(\/)?$/,
    color: '#dd2a7b',
    tooltip: 'Instagram',
  },
  {
    id: 'website_url',
    icon: BsGlobe,
    label: 'Website',
    regex:
      /https?:\/\/[\w\-_]+(\.[\w\-_]+)+([\w\-\.,@?^=%&:/~\+#]*[\w\-\@?^=%&/~\+#])?/, // eslint-disable-line no-useless-escape
    tooltip: 'Website',
  },
  { id: 'ashby_url', icon: Ashby, skipForForm: true, tooltip: 'Ashby' },
  { id: 'lever_url', icon: Lever, skipForForm: true, tooltip: 'Lever' },
  {
    id: 'greenhouse_url',
    icon: Greenhouse,
    skipForForm: true,
    tooltip: 'Greenhouse',
  },
  {
    id: 'redirection_url',
    icon: HiArrowTopRightOnSquare,
    skipForForm: true,
    tooltip: 'View profile',
    color: '#6D659D',
  },
];

interface StageDetails {
  stages: any[];
  stageId: number;
  daysInStage: number;
}

interface props {
  contactId: number;
  handleContactFetch?: (_contact: any) => void;
  candidate?: any;
  currentJob?: any;
  stageDetails?: StageDetails;
}

export const KContactCard = ({
  candidate,
  currentJob,
  stageDetails,
}: props) => {
  const contact = {
    name: candidate?.name,
    position: candidate?.position,
    phone: candidate?.local_phone_number || 'Not available',
    emails: candidate?.emails ? candidate?.emails : [],
    email: candidate?.email,
    image_url: candidate?.image_url || '',
    social: candidate?.social || {},
  };

  const selectedStage =
    stageDetails?.stages?.find(
      (stage) => stage?.id === stageDetails?.stageId
    ) || null;

  const socialUrls = Object?.keys(contact?.social)
    ?.filter((key) => key?.endsWith('_url'))
    ?.map((key) => ({
      type: key,
      url: contact?.social?.[key],
    }));

  const candidateUrls = [
    ...socialUrls,
    {
      type: 'redirection_url',
      url: `${window.location.origin}/contacts/${candidate?.id}`,
    },
  ];

  const renderSocialLinks = () => (
    <Flex alignItems="center" gap="8px" py="0">
      {candidateUrls?.map((candidateUrl) => {
        if (!candidateUrl?.url) return null;
        const {
          icon = BsGlobe,
          color = '#000',
          tooltip = '',
        } = candidateLinksConfig?.find(
          (config) => config.id === candidateUrl?.type
        ) || {};
        return (
          <Tooltip
            key={candidateUrl?.type}
            label={tooltip || candidateUrl?.['tooltip']}
            placement="bottom"
          >
            <Link
              key={candidateUrl?.type}
              href={candidateUrl?.url}
              target="_blank"
              rel="noopener noreferrer"
              _focus={{ outline: 'none' }}
              onClick={(e) => e?.stopPropagation()}
            >
              <Center>
                {/* @ts-expect-error - icon is a valid IconType */}
                <Icon as={icon} color={color} width="16px" height="16px" />
              </Center>
            </Link>
          </Tooltip>
        );
      })}
    </Flex>
  );

  const renderEmails = () => {
    return (
      <>
        {contact?.emails?.length === 0 && (
          <div>
            <KText>No emails available</KText>
          </div>
        )}
        {contact?.emails?.length > 0 && (
          <div>
            {contact?.emails?.map((emailItem: any) => (
              <Flex alignItems="center" gap="4px" key={emailItem?.id}>
                <Icon
                  as={Email}
                  color="ats-primary"
                  width="16px"
                  height="16px"
                  mr="4px"
                />
                <TruncatedTextWithTooltip
                  maxW={'220px'}
                  toolTipVariant="default"
                  text={emailItem?.email}
                  cursor="pointer"
                />
                {emailItem?.verified && (
                  <Tooltip
                    hasArrow
                    label="This email is verified"
                    placement="bottom"
                  >
                    <Center as="span">
                      <CheckCircle size={16} className="kula-icon" />
                    </Center>
                  </Tooltip>
                )}
              </Flex>
            ))}
          </div>
        )}
      </>
    );
  };
  const renderEmail = () => {
    return (
      <>
        {contact?.email?.length === 0 && (
          <div>
            <KText>No email available</KText>
          </div>
        )}
        {contact?.email?.length > 0 && (
          <div>
            <Flex alignItems="center" gap="4px">
              <Icon
                as={Email}
                color="ats-primary"
                width="16px"
                height="16px"
                mr="4px"
              />
              <TruncatedTextWithTooltip
                maxW={'220px'}
                toolTipVariant="default"
                text={contact?.email}
                cursor="pointer"
              />
            </Flex>
          </div>
        )}
      </>
    );
  };

  return (
    <Flex flexDir="column">
      <Flex gap="12px">
        <Avatar
          src={contact?.image_url}
          w="52px"
          h="52px"
          name={contact?.name}
          borderRadius="sm"
        />
        <Flex flexDirection="column">
          <TruncatedTextWithTooltip
            maxW={'220px'}
            size="xxl-semi-bold"
            color="secondary-900"
            toolTipVariant="default"
            textTransform="capitalize"
            text={contact?.name}
            cursor="pointer"
          />
          <KText>{contact?.position}</KText>
        </Flex>
      </Flex>
      <Flex mt="20px" gap="12px" flexDirection="column">
        {renderSocialLinks()}
        {contact?.emails?.length > 0 && renderEmails()}
        {contact?.email?.length > 0 && renderEmail()}
        <Flex gap="4px" alignItems={'center'}>
          <Icon
            // @ts-expect-error - icon is a valid IconType
            as={HiOutlinePhone}
            color="ats-primary"
            width="16px"
            height="16px"
            mr="4px"
          />
          <KText>{contact?.phone}</KText>
        </Flex>
      </Flex>

      <Flex gap="6px" mt="20px" flexDirection="column">
        <KText fontWeight={650} fontSize="12px" color="secondary-600">
          CURRENT JOB
        </KText>
        <KText size="sm-semi-bold" color="primary-500">
          <Link
            href={`/jobs/${currentJob?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            textDecoration="none"
          >
            <Flex alignItems="center" gap="4px">
              <TruncatedTextWithTooltip
                maxW={'220px'}
                toolTipVariant="default"
                text={currentJob?.name}
                cursor="pointer"
                size="sm-semi-bold"
                color="primary-500"
              />
              <Icon
                _hover={{ color: 'ats-primary' }}
                // @ts-expect-error - icon is a valid IconType
                as={HiArrowTopRightOnSquare}
                w="14px"
                h="14px"
                display="flex"
              />
            </Flex>
          </Link>
        </KText>
        {selectedStage?.name && (
          <Flex alignItems="center" gap="12px">
            <KTag borderRadius="xs" value={selectedStage?.name}></KTag>
            <Flex alignItems="center" gap="5px">
              <Clock size={18} className="kula-icon" />
              <KText size="sm-medium" color="secondary-700">
                {stageDetails?.daysInStage} days
              </KText>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
